import React, { useEffect, useState, memo } from 'react';
import { Tooltip, Radio, Button } from 'antd';
import { useSelector, useDispatch } from 'react-redux';

// import '@style/modules/analysis/esg.scss';
import '../../../../styles/modules/analysis/esg.scss';
import { Summary } from '@components/summary';
import { EditableScoreBar } from './scoreBar';
import ScoreIcon from './scoreIcon';
import { updateESGSummary } from '@service/apis/analysis.api';
import { deepCopy } from '@utils/common';
import actions from '@flux/action';
import { API } from '@service';
import { BASE_URL } from '@service/apis/index';
import { axios as apiService } from '@service/axiosService';
import { calculateESGData } from './esgHelper';
import SectionComment from 'src/ddvComment/SectionComment';
import CommentAnchor from 'src/ddvComment/directUser/CommentAnchor';

const Model = [
  [
    {
      text: 'Community',
      key: 'community',
      tooltip:
        "The Community Category covers the company's commitment and effectiveness within the local, national and global community in which it does business. It reflects a company's citizenship, charitable giving, and volunteerism. This category covers the company's human rights record and treatment of its supply chain. It also covers the environmental and social impacts of the company's products and services, and the development of sustainable products, processes and technologies.",
    },
    {
      text: 'Community Dev & Philanthropy',
      key: 'communityDevPhilanthropy',
      tooltip:
        'The Community Development and Philanthropy subcategory covers the relationship between a company and the communities within which it is embedded. It reflects a company’s community citizenship through charitable giving, donations of goods, and volunteerism of staff time. It also includes protecting public health (e.g., avoidance of industrial accidents) and managing the social impacts of its operations on local communities. The subcategory also includes a company’s land use and building design impact on the local economy and ecosystem.',
    },
    {
      text: 'Product',
      key: 'product',
      tooltip:
        'The Product subcategory covers the responsibility of a company for the development, design, and management of its products and services and their impacts on customers and society at large. This subcategory reflects a company’s capacity to reduce environmental costs, create new market opportunities through new sustainable technologies or processes, and produce or market goods and services that enhance the health and quality of life for consumers. This subcategory rating covers the integrity of a company’s products and sales practices, including their labeling and marketing, social impacts and end-of-life disposition. It also relates to product safety and quality and the company’s response to problems with safety and quality.',
    },
    {
      text: 'Human Rights & Supply Chain',
      key: 'humanRightsSupplyChain',
      tooltip:
        'The Human Rights and Supply Chain subcategory measures a company’s commitment to respecting fundamental human rights conventions, its ability to maintain its license to operate by supporting freedom of association and excluding child, forced or compulsory labor. This subcategory covers a company’s transparency in overseas sourcing disclosure and monitoring and a company’s relationship with and respect for the human rights of indigenous peoples near its proposed or current operations.',
    },
  ],
  [
    {
      text: 'Employees',
      key: 'employee',
      tooltip: `The Employees Category includes disclosure of policies, programs, and performance in diversity, labor relations and labor rights, compensation, benefits, and employee training, health and safety. The evaluation focuses on the quality of policies and programs, compliance with national laws and regulations, and proactive management initiatives. 
        The category includes evaluation of inclusive diversity policies, fair treatment of all employees, robust diversity (EEO-1) programs and training, disclosure of workforce diversity data, strong labor codes (addressing the core ILO standards), comprehensive benefits, demonstrated training and development opportunities, employee health and safety policies, basic and industry-specific safety training, demonstrated safety management systems, and a positive safety performance record.`,
    },
    {
      text: 'Compensation & Benefits',
      key: 'compensationBenefits',
      tooltip:
        'The Compensation and Benefits subcategory covers a company’s capacity to increase its workforce loyalty and productivity through rewarding, fair, and equal compensation and financial benefits. It includes benefits that engage employees and improve worker development. This subcategory also focuses on long-term employment growth and stability by promotion practices, lay-off practices, and relations with retired employees.',
    },
    {
      text: 'Diversity & Labor Rights',
      key: 'diversityLaborRights',
      tooltip:
        'The Diversity and Labor Rights subcategory covers workplace policies and practices covering fair and non-discriminatory treatment of employees, and its diversity policies. It covers a company’s labor-management relations and participation by employees, National Labor Relations Board (NLRB) violations or patterns of anti-union practice, conformance to internationally recognized worker rights, as defined in the basic conventions of the International Labor Organization (ILO). Fundamental labor rights include freedom of association and protection of the right to organize; right to bargain collectively; a minimum age for the employment of children; a prohibition against forced labor; lack of employment and occupational discrimination; and equal compensation. This subcategory measures a company’s ability to maintain diversity, provide equal opportunities regardless of gender, age, ethnicity, religion or sexual orientation, and promote work-life balance.',
    },
    {
      text: 'Training, Health & Safety',
      key: 'trainingHealthSafety',
      tooltip:
        'The Training, Safety and Health subcategory measures a company’s effectiveness in providing a healthy and safe workplace. This subcategory includes accident and safety performance, as well as job training, safety standards and training, and employee-management safety teams. It includes programs to support the health, well-being and productivity of all employees. This subcategory includes workplace policies and programs that boost employee morale, workplace productivity, company policies and practices to engage employees, and worker development.',
    },
  ],
  [
    {
      text: 'Environment',
      key: 'environment',
      tooltip:
        "The Environment Category data covers a company's interactions with the environment at large, including use of natural resources, and a company's impact on the Earth's ecosystems. The category evaluates corporate environmental performance, compliance with environmental regulations, mitigation of environmental footprint, leadership in addressing climate change through appropriate policies and strategies, energy-efficient operations, and the development of renewable energy and other alternative environmental technologies, disclosure of sources of environmental risk and liability and actions to minimize exposure to future risk, implementation of natural resource conservation and efficiency programs, pollution prevention programs, demonstration of a strategy toward sustainable development, integration of environmental sustainability and responsiveness with management and the board, and programs to measure and engage stakeholders for environmental improvement.",
    },
    {
      text: 'Energy & Climate Change',
      key: 'energyClimateChange',
      tooltip:
        'The Energy and Climate Change subcategory measures a company’s effectiveness in addressing climate change through appropriate policies and strategies, energy-efficient operations, and the development of renewable energy and other alternative environmental technologies. The subcategory includes energy use, emissions to air of CO2 and other Greenhouse Gas Emissions (GHG).',
    },
    {
      text: 'Environment Policy & Reporting',
      key: 'environmentPolicyReporting',
      tooltip:
        'The Environmental Policy and Reporting subcategory includes a company’s policies and intention to reduce the environmental impact of a company and its value stream to levels that are healthy for the company and for the environment, now and in the future. The data includes the company’s environmental reporting performance, adherence to environmental reporting standards such as the Global Reporting Initiative, and compliance with investor, regulatory and stakeholders’ requests for transparency. Compliance data consists of breaches of regulatory limits and accidental releases.',
    },
    {
      text: 'Resource Management',
      key: 'resourceManagement',
      tooltip:
        'The Resource Management subcategory covers how efficiently resources are used in manufacturing and delivering products and services, including those of a company’s suppliers. It includes a company’s capacity to reduce the use of materials, energy or water, and to find more efficient solutions by improving its supply chain management. This subcategory includes environmental performance relative to production size and is monitored by the production-related Eco Intensity Ratios (EIRs) for water and energy defined as resource consumption per produced or released unit. Resource materials include raw materials and packaging materials for production and related processes and packaging of products. Resource Management data also include waste and recycling performance. Recycling data is related to the proportion of waste recycled of the total waste. Data includes how the company manages operations to benefit the local airshed and watershed, and how the company impacts land use and local ecological stability. The water resource data includes consumption of drinking water, industrial water and steam.',
    },
  ],
  [
    {
      text: 'Governance',
      key: 'governance',
      tooltip:
        "The Governance Category covers disclosure of policies and procedures, board independence and diversity, executive compensation, attention to stakeholder concerns, and evaluation of a company's culture of ethical leadership and compliance. Corporate governance refers to leadership structure and the values that determine corporate direction, ethics and performance. This category rates factors such as: are corporate policies and practices aligned with sustainability goals; is the management of the corporation transparent to stakeholders; are employees appropriately engaged in the management of the company; are sustainability principles integrated from the top down into the day-to-day operations of the company. Governance focuses on how management is committed to sustainability and corporate responsibility at all levels.",
    },
    {
      text: 'Board',
      key: 'board',
      tooltip:
        'The Board subcategory covers a company’s effectiveness in following best practices in corporate governance principles related to board membership, independent decision making through experienced, diverse and independent board members, effectiveness toward following best practices related to board activities and functions, and board committee structure and composition. It includes how the company provides competitive and proportionate management compensation and its ability to incent executives and board members to achieve both financial and extra-financial targets.',
    },
    {
      text: 'Leadership Ethics',
      key: 'leadershipEthics',
      tooltip:
        'The Leadership Ethics subcategory measures how a company manages its relationships with its various stakeholders, including investors, customers, communities, and regulators. This subcategory measures a company’s effectiveness in treating its shareholders equitably. Leadership ethics includes the company’s culture of ethical decision making. It measures a company’s commitment and effectiveness toward the vision of integrating social and environmental aspects into the overall core strategy and whether sustainability principles are integrated from the top down into the day-to-day operations of the company.',
    },
    {
      key: 'transparencyReporting',
      text: 'Transparency & Reporting',
      tooltip:
        'The Transparency and Reporting subcategory rates factors including are corporate policies and practices aligned with sustainability goals, is the management of the corporation transparent to stakeholders, are employees appropriately engaged in the management of the company, and do sustainability reports  comply with standards such as the Global Reporting Initiative, AccountAbility (AA1000) and other standards, and are these reports made publicly available. This subcategory includes whether the company provides a list of its major stakeholders and how it engages with them. It also covers whether the company is a signatory of the Global Compact and other leading global entities. It evaluates the assurance (3rd party audit) of the accuracy, completeness, and reliability of its Sustainability or Corporate Social Responsibility reports.',
    },
  ],
];

const TitleText = ({ text, tooltip, onLeft = false }) =>
  tooltip ? (
    <Tooltip
      title={tooltip}
      overlayClassName="esg-csr-title-tooltip"
      getPopupContainer={() =>
        document.getElementsByClassName('esg-categories')[0] || document.body
      }
      placement={onLeft ? 'left' : 'top'}
    >
      <span className="esg-category-text">{text}</span>
    </Tooltip>
  ) : (
    <span className="esg-category-text">{text}</span>
  );

const CategoryCard = ({
  info,
  buyerLabel,
  targetLabel,
  edit,
  handleChange,
  index,
}) => (
  <div className="esg-comparison-div">
    <div className="comparison-role-indicator">
      <div className="esg-role-comparison-wrapper">
        <span />
        <span>{buyerLabel}</span>
        <span />
        <span>{targetLabel}</span>
        <span />
      </div>
    </div>
    <div className="esg-comparison-card">
      {info.map(({ key, text, buyer, target, tooltip }, i) => (
        <div className="esg-comparison-row" key={text}>
          <TitleText text={text} tooltip={tooltip} onLeft={index === 3} />
          <div className="score-circles-in-categories">
            <div />
            <ScoreIcon
              score={buyer}
              key={key}
              edit={edit}
              onChange={handleChange(key, true)}
            />
            {edit ? null : (
              <>
                <div />
                <div />
              </>
            )}
            <ScoreIcon
              score={target}
              key={1}
              edit={edit}
              onChange={handleChange(key, false)}
            />
            <div />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const ECGandCSR = ({ data, refetch, currentSubmoduleName }) => {
  const summary = data?.summary || '';
  const dataSrc = calculateESGData(data);
  const { esgManualViewFlag } = data;

  const dispatch = useDispatch();
  const [isEditView, setIsEditView] = useState(esgManualViewFlag);
  const [edit, setIsEdit] = useState(false);
  const [editSummery, setEditSummery] = useState(false);
  const [draftData, setDraftData] = useState(deepCopy(dataSrc));
  const projectId = useSelector((state) => state.common.currentId);
  const isClient = JSON.parse(window.localStorage.userInfo || '{}')?.isClient;

  const { buyerInfo, targetInfo } = draftData || dataSrc;

  const { buyerLabel, targetLabel } = useSelector(({ common }) => ({
    buyerLabel: common.projectCompanyInfo.buyerLabel,
    targetLabel: common.projectCompanyInfo.targetLabel,
  }));

  const overallInfo = draftData.overallInfo.map((e, i) =>
    Object.assign(e, { role: i === 0 ? buyerLabel || '' : targetLabel || '' }),
  );

  const catesInfo = Model.map((catInfo) =>
    catInfo.map((e) =>
      Object.assign(e, {
        key: e.key,
        buyer: buyerInfo[e.key],
        target: targetInfo[e.key],
      }),
    ),
  );

  const toggleEdit = async (status) => {
    if (typeof status !== 'boolean') {
      return;
    }
    dispatch(actions.changeLoading(true));
    await API.toggleESGEditStatus(projectId, status);
    await refetch();
    dispatch(actions.changeLoading(false));
  };

  useEffect(() => {
    toggleEdit(isEditView);
  }, [isEditView]);

  useEffect(() => {
    setDraftData(deepCopy(calculateESGData(data, isEditView)));
  }, [data]);

  const switchEdit = (e) => {
    setIsEditView(Boolean(e.target.value));
  };

  const handleCancel = () => {
    setDraftData(deepCopy(calculateESGData(data, isEditView)));
    setIsEdit(false);
  };

  const handleSelectChange = (field, isBuyer) => (value) => {
    setDraftData((draft) => {
      const newDraft = deepCopy(draft);
      if (isBuyer) {
        newDraft.buyerInfo[field] = value;
      } else {
        newDraft.targetInfo[field] = value;
      }
      return newDraft;
    });
  };

  const handleSaveDraft = async () => {
    await apiService.post(`${BASE_URL}/project/esg/${projectId}`, {
      buyerUDCsr: {
        ...draftData.buyerInfo,
        overall: draftData.overallInfo[0].score,
        isOverallNotApplicable: draftData.overallInfo[0].isOverallNotApplicable,
      },
      targetUDCsr: {
        ...draftData.targetInfo,
        overall: draftData.overallInfo[1].score,
        isOverallNotApplicable: draftData.overallInfo[1].isOverallNotApplicable,
      },
    });
    await refetch();
    dispatch(actions.changeLoading(true));
    setIsEdit(false);
    dispatch(actions.changeLoading(false));
  };

  useEffect(() => {
    if (isClient) {
      setIsEditView(true);
    }
  }, [isClient]);

  return (
    <>
      <div className="section-comment-wrapper-in-sentiment">
        <SectionComment module={currentSubmoduleName} placement="bottomRight" />
      </div>
      <div className="esg-csr-container">
        <div className="upper-wrapper">
          <div className="upper-left-wrapper">
            <div className="rating-title">
              <span className="overall-rating">Overall Rating</span>
              <div className="rating-space" />
              <Radio.Group
                onChange={switchEdit}
                value={isEditView ? 1 : 0}
                className="role-control"
              >
                <Radio value={0} disabled={edit}>
                  Default
                </Radio>
                <Radio value={1}>Edited</Radio>
              </Radio.Group>
            </div>
            <div className="compare-how-companie">
              <p>
                Compare how companies perform on sustainability and CSR issues.
              </p>
              <p>
                Hover over each of the categories or subcategories for more
                information.
              </p>
            </div>
            <div className="sub-title-card">
              {overallInfo.map(
                ({ role, companyName, score, p1, p2, p3, p4 }, i) => (
                  <EditableScoreBar
                    key={`${role}-score-bar`}
                    role={role}
                    name={companyName}
                    mean={score}
                    point1={p1}
                    point2={p2}
                    point3={p3}
                    point4={p4}
                    isEditable={edit}
                    isOverallNotApplicable={
                      overallInfo[i].isOverallNotApplicable
                    }
                    onChange={(v) =>
                      setDraftData((draft) => {
                        const newDraft = deepCopy(draft);
                        newDraft.overallInfo[i].score = v;
                        return newDraft;
                      })
                    }
                    onNotApplicableChange={(v) =>
                      setDraftData((draft) => {
                        const newDraft = deepCopy(draft);
                        newDraft.overallInfo[i].isOverallNotApplicable = v;
                        return newDraft;
                      })
                    }
                  />
                ),
              )}
            </div>
          </div>

          <div className="upper-right-wrapper">
            {isClient ? null : (
              <>
                {!isEditView ? null : edit ? (
                  <div className="save-esg-wrapper">
                    <Button onClick={handleCancel}>cancel</Button>
                    <Button
                      type="primary"
                      onClick={handleSaveDraft}
                      loading={false}
                    >
                      save
                    </Button>
                  </div>
                ) : editSummery ? (
                  <div className="save-esg-wrapper role-control">
                    <Button disabled={true}>Edit</Button>
                  </div>
                ) : (
                  <div className="save-esg-wrapper role-control">
                    <Button
                      type="primary"
                      onClick={() => setIsEdit(true)}
                      loading={false}
                    >
                      edit
                    </Button>
                  </div>
                )}
              </>
            )}
            <Summary
              text={summary}
              onUpdate={refetch}
              saveSummary={updateESGSummary}
              valueEdit={isClient || edit}
              editState={setEditSummery}
            />
          </div>
        </div>

        <div className="esg-categories">
          {catesInfo.map((i, index) => (
            <CategoryCard
              info={i}
              key={`cate-${index}}`}
              buyerLabel={buyerLabel}
              targetLabel={targetLabel}
              edit={edit}
              handleChange={handleSelectChange}
              index={index}
            />
          ))}
        </div>
        <div className="esg-source-disclaimer">
          Source: CSRHub, Consensus ESG Ratings
        </div>
      </div>
    </>
  );
};

export default CommentAnchor(memo(ECGandCSR), {
  shouldScrollInToView: (anchorRecord, anchorId) =>
    anchorRecord?.moduleName === anchorId,
  getAnchorIdFromProp(props) {
    return props.currentSubmoduleName;
  },
});
